import { useCallback, useContext } from 'react';

import { RootActionType } from './actions';
import { RootContext } from './context';
import {
  Entity,
  LinesheetState,
  Row,
  SortDirection,
  SortKey,
  SortScope,
} from './reducers/linesheet';

export function useBlurLinesheetInDOM() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.BlurLinesheetInDOM,
    });
  }, []);
}

export function useClearLinesheetRows() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ClearLinesheetRows,
    });
  }, []);
}

export function useFillLinesheetWithEmptyCasepacks() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.FillLinesheetWithEmptyCasepacks,
    });
  }, []);
}

export function useFillLinesheetWithEmptyItems() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.FillLinesheetWithEmptyItems,
    });
  }, []);
}

export function useFocusLinesheetInDOM() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.FocusLinesheetInDOM,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellBackward() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellBackward,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellDown() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellDown,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellForward() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellForward,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellLeft() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellLeft,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellRight() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellRight,
    });
  }, []);
}

export function useMoveLinesheetFocusedCellUp() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.MoveLinesheetFocusedCellUp,
    });
  }, []);
}

export function useReceiveLinesheetAttributes() {
  const { dispatch } = useContext(RootContext);

  return useCallback(
    (payload: readonly { label: string; inturnType: string }[]) => {
      dispatch({
        payload,
        type: RootActionType.ReceiveLinesheetAttributes,
      });
    },
    [],
  );
}

export function useReceiveLinesheetColumnCount() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: number) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetColumnCount,
    });
  }, []);
}

export function useReceiveLinesheetFilterKey() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: string) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetFilterKey,
    });
  }, []);
}

export function useReceiveLinesheetFilterRollups() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: LinesheetState['filterRollups']) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetFilterRollups,
    });
  }, []);
}

export function useReceiveLinesheetRows() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: Row[]) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetRows,
    });
  }, []);
}

export function useReceiveLinesheetTotalCasepackCasesInView() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: number | null) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetTotalCasepackCasesInView,
    });
  }, []);
}

export function useReceiveLinesheetTotalUnitsInView() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: number) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetTotalUnitsInView,
    });
  }, []);
}

export function useReceiveLinesheetAvailableQuantity() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: number) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveLinesheetAvailableQuantity,
    });
  }, []);
}

export function useReceiveMoreLinesheetRows() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: { offset: number; rows: Row[] }) => {
    dispatch({
      payload,
      type: RootActionType.ReceiveMoreLinesheetRows,
    });
  }, []);
}

export function useRemoveLinesheetSort() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.RemoveLinesheetSort,
    });
  }, []);
}

export function useResetLinesheetFocus() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ResetLinesheetFocus,
    });
  }, []);
}

export function useResetLinesheetSelections() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ResetLinesheetSelections,
    });
  }, []);
}

export function useResetLinesheetSort() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ResetLinesheetSort,
    });
  }, []);
}

export function useSetIsLinesheetStale() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: boolean) => {
    dispatch({
      payload,
      type: RootActionType.SetIsLinesheetStale,
    });
  }, []);
}

export function useSetIsSimulatingLoading() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: boolean) => {
    dispatch({
      payload,
      type: RootActionType.SetIsSimulatingLoading,
    });
  }, []);
}

export function useSetLinesheetEntity() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: Entity) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetEntity,
    });
  }, []);
}

export function useSetLinesheetFocusedCell() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: { column: number; row: number }) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetFocusedCell,
    });
  }, []);
}

export function useSetLinesheetHiddenColumns() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: Set<string>) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetHiddenColumns,
    });
  }, []);
}

export function useSetLinesheetSavedMarketUuid() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: string) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetSavedMarketUuid,
    });
  }, []);
}

export function useSetLinesheetSavedUploadChannelUuid() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: string) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetSavedUploadChannelUuid,
    });
  }, []);
}

export function useSetLinesheetScrollToIndex() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: number) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetScrollToIndex,
    });
  }, []);
}

export function useSetLinesheetSort() {
  const { dispatch } = useContext(RootContext);

  return useCallback(
    (payload: {
      direction: SortDirection | null;
      key: SortKey;
      scope: SortScope | null;
      scopeValue: string | null;
    }) => {
      dispatch({
        payload,
        type: RootActionType.SetLinesheetSort,
      });
    },
    [],
  );
}

export function useSetLinesheetViewableIndexes() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: { startIndex: number; stopIndex: number }) => {
    dispatch({
      payload,
      type: RootActionType.SetLinesheetViewableIndexes,
    });
  }, []);
}

export function useToggleMobileFiltersModal() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ToggleMobileFiltersModal,
    });
  }, []);
}

export function useToggleSelectAllLinesheetRows() {
  const { dispatch } = useContext(RootContext);

  return useCallback(() => {
    dispatch({
      type: RootActionType.ToggleSelectAllLinesheetRows,
    });
  }, []);
}

export function useToggleSelectLinesheetRow() {
  const { dispatch } = useContext(RootContext);

  return useCallback((payload: string) => {
    dispatch({
      payload,
      type: RootActionType.ToggleSelectLinesheetRow,
    });
  }, []);
}

export function useSetIsUploadInventoryModalOpen() {
  const { dispatch } = useContext(RootContext);

  return useCallback((isOpen: boolean) => {
    dispatch({
      type: RootActionType.ToggleUploadInventoryModal,
      payload: isOpen,
    });
  }, []);
}
